import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { SDialog } from '@modules/ui-components';
import type { AuthorDoc } from '../../services/AuthorFirestoreService';
import { authorCloneService } from '../../services/AuthorCloneService';

export interface CloneAuthorDialogProps {
  open: boolean;
  projectId: string;
  sourceAuthor: AuthorDoc | null;
  onClose: () => void;
  onCloned: (authorId: string) => void;
}

export const CloneAuthorDialog: React.FC<CloneAuthorDialogProps> = ({
  open,
  projectId,
  sourceAuthor,
  onClose,
  onCloned,
}) => {
  const suggestedName = useMemo(
    () => (sourceAuthor?.displayName ? `${sourceAuthor.displayName} copy` : ''),
    [sourceAuthor?.displayName],
  );
  const [displayName, setDisplayName] = useState(suggestedName);
  const [includePosts, setIncludePosts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDisplayName(suggestedName);
    setIncludePosts(false);
    setError(null);
  }, [open, suggestedName]);

  const canSubmit = Boolean(sourceAuthor?.id && displayName.trim()) && !submitting;

  const handleClone = async () => {
    if (!sourceAuthor?.id || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await authorCloneService.cloneAuthor({
        projectId,
        sourceAuthorId: sourceAuthor.id,
        displayName,
        includePosts,
      });
      onCloned(result.newAuthorId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SDialog
      open={open}
      onClose={() => {
        if (!submitting) onClose();
      }}
      title="Clone author"
      maxWidth="sm"
      fullWidth
      data-testid="clone-author-dialog"
      primaryAction={{
        label: 'Clone',
        onClick: handleClone,
        loading: submitting,
        disabled: !canSubmit,
        'data-testid': 'clone-author-confirm',
      }}
      secondaryAction={{
        label: 'Cancel',
        onClick: onClose,
        disabled: submitting,
      }}
    >
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        <Typography variant="body2" color="text.secondary">
          Platforms, voice, design settings, templates, examples, and pillars will be copied into a new author workspace.
        </Typography>
        <TextField
          label="New author name"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          autoFocus
          fullWidth
          inputProps={{ 'data-testid': 'clone-author-name' }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={includePosts}
              onChange={(event) => setIncludePosts(event.target.checked)}
              inputProps={{ 'data-testid': 'clone-author-include-posts' } as any}
            />
          }
          label="Include scraped post corpus"
        />
      </Stack>
    </SDialog>
  );
};
